package com.evoapps.evoshift.viewmodels

import com.evoapps.evoshift.domain.util.AppClock
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.model.AllocationBasis
import com.evoapps.evoshift.domain.model.AllocationMethod
import com.evoapps.evoshift.domain.model.CostFrequency
import com.evoapps.evoshift.domain.model.LedgerKind
import com.evoapps.evoshift.domain.model.RecurringCostRule
import com.evoapps.evoshift.domain.model.SharedCost
import com.evoapps.evoshift.domain.model.SharedCostAllocation
import com.evoapps.evoshift.domain.model.SharedCostFrequency
import com.evoapps.evoshift.domain.model.basisFor
import com.evoapps.evoshift.domain.model.capabilities
import com.evoapps.evoshift.domain.usecase.GetWorkplaceUseCase
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import com.evoapps.evoshift.domain.usecase.owner.BusinessMonth
import com.evoapps.evoshift.domain.usecase.owner.DeleteSharedCostUseCase
import com.evoapps.evoshift.domain.usecase.owner.GetBusinessMonthUseCase
import com.evoapps.evoshift.domain.usecase.owner.GetSharedCostsUseCase
import com.evoapps.evoshift.domain.usecase.owner.SaveSharedCostUseCase
import com.evoapps.evoshift.domain.usecase.settings.GetRecurringCostRulesUseCase
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.shared_cost_converted
import evoshift.composeapp.generated.resources.shared_cost_deleted
import evoshift.composeapp.generated.resources.shared_cost_saved
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.YearMonth
import kotlinx.datetime.number
import kotlinx.datetime.todayIn

/**
 * SH-131: the owner's shared costs of the business ("Koszty wspólne firmy"), split across the
 * workplaces they own. [preview] is this month's figures per workplace, for the editor's live
 * split; [business] the month across the workplaces, shared costs counted once.
 */
data class SharedCostsUiState(
    val isLoading: Boolean = true,
    val costs: List<SharedCost> = emptyList(),
    /** The workplaces the person owns, in their order: what a cost may be split across. */
    val workplaces: Map<String, WorkplaceInfo> = emptyMap(),
    /** This month, for the preview and the totals. */
    val month: YearMonth? = null,
    /** This month's figure per workplace for each proportional basis (the preview's split). */
    val basis: Map<AllocationBasis, Map<String, Double>> = emptyMap(),
    /** This month across the owned workplaces; null until read. */
    val business: BusinessMonth? = null,
    /** A per-workplace cost rule being converted (the editor opens filled in from it). */
    val converting: RecurringCostRule? = null,
    val isSaving: Boolean = false,
    /** Set when a save went through: the editor closes. */
    val savedAt: Int = 0,
    val error: UiText? = null,
    val successMessage: UiText? = null
)

sealed interface SharedCostsEvent {
    data class Save(val cost: SharedCost, val replacesRuleId: String? = null) : SharedCostsEvent
    data class Delete(val costId: String) : SharedCostsEvent
    data object ConversionDismissed : SharedCostsEvent
    data object MessageShown : SharedCostsEvent
    data object Refresh : SharedCostsEvent
}

class SharedCostsViewModel(
    private val getCurrentUserUseCase: GetCurrentUserUseCase,
    private val getWorkplaceUseCase: GetWorkplaceUseCase,
    private val getSharedCosts: GetSharedCostsUseCase,
    private val saveSharedCost: SaveSharedCostUseCase,
    private val deleteSharedCost: DeleteSharedCostUseCase,
    private val getBusinessMonth: GetBusinessMonthUseCase,
    private val getRecurringCostRules: GetRecurringCostRulesUseCase? = null,
    /** The rule to convert ("convert to a shared cost" on a monthly cost), from the route. */
    private val convertRuleId: String? = null,
    private val today: () -> LocalDate = { AppClock.todayIn(TimeZone.currentSystemDefault()) }
) : ViewModel() {

    private val _uiState = MutableStateFlow(SharedCostsUiState())
    val uiState: StateFlow<SharedCostsUiState> = _uiState.asStateFlow()
    private var conversionOffered = false

    init {
        load()
    }

    fun onEvent(event: SharedCostsEvent) {
        when (event) {
            is SharedCostsEvent.Save -> save(event.cost, event.replacesRuleId)
            is SharedCostsEvent.Delete -> delete(event.costId)
            SharedCostsEvent.ConversionDismissed -> _uiState.update { it.copy(converting = null) }
            SharedCostsEvent.MessageShown -> _uiState.update { it.copy(error = null, successMessage = null) }
            SharedCostsEvent.Refresh -> load()
        }
    }

    /** A new cost as the editor starts it: monthly from this month, split equally across every workplace. */
    fun blank(currency: String = defaultCurrency()): SharedCost {
        val month = _uiState.value.month ?: monthOf(today())
        return SharedCost(
            id = "", name = "", amount = 0.0, currency = currency,
            frequency = SharedCostFrequency.MONTHLY,
            date = today().toString(),
            startMonth = month.toString(),
            method = AllocationMethod.PROPORTIONAL,
            basis = AllocationBasis.EQUAL,
            workplaceIds = _uiState.value.workplaces.keys.toList()
        )
    }

    /**
     * The editor filled in from a per-workplace rule: its name and amount (a weekly rule as its
     * monthly average), from this month, wholly that workplace's until the owner splits it.
     */
    fun fromRule(rule: RecurringCostRule): SharedCost {
        val month = _uiState.value.month ?: monthOf(today())
        val (frequency, amount) = when (rule.frequency) {
            CostFrequency.YEARLY -> SharedCostFrequency.YEARLY to rule.amount
            CostFrequency.WEEKLY -> SharedCostFrequency.MONTHLY to SharedCostAllocation.round2(rule.amount * 52 / 12)
            else -> SharedCostFrequency.MONTHLY to rule.amount
        }
        return SharedCost(
            id = "", name = rule.name, amount = amount, currency = rule.currency,
            frequency = frequency, date = today().toString(), startMonth = month.toString(),
            method = AllocationMethod.PERCENTAGES,
            workplaceIds = _uiState.value.workplaces.keys.toList(),
            shares = _uiState.value.workplaces.keys.associateWith { if (it == rule.targetWorkplaceId) 100.0 else 0.0 }
        )
    }

    private fun defaultCurrency() = _uiState.value.workplaces.values.firstOrNull()?.currency ?: DEFAULT_CURRENCY

    private fun load() {
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = it.costs.isEmpty()) }
            val user = getCurrentUserUseCase().first() ?: return@launch _uiState.update { it.copy(isLoading = false) }
            val workplaces = linkedMapOf<String, WorkplaceInfo>()
            // The owner's own workplaces only: a co-owner's OWNER engagement is not theirs to split into.
            user.engagements.values
                .filter { it.capabilities.shows(Gate.RECURRING_COSTS) }
                .forEach { engagement ->
                    getWorkplaceUseCase(engagement.workplaceId).onSuccess { wp ->
                        if (wp.ownerId == user.uid) workplaces[wp.id] = WorkplaceInfo(wp.id, wp.name, wp.settings.currency)
                    }
                }
            val month = monthOf(today())
            _uiState.update { it.copy(workplaces = workplaces, month = month) }
            if (workplaces.isEmpty()) return@launch _uiState.update { it.copy(isLoading = false, costs = emptyList()) }

            getSharedCosts(user.uid).fold(
                onSuccess = { costs -> _uiState.update { it.copy(isLoading = false, costs = costs) } },
                onFailure = { e -> _uiState.update { it.copy(isLoading = false, error = e.toUiText()) } }
            )
            loadMonth(workplaces.keys.toList(), month)
            offerConversion(user.uid)
        }
    }

    /** This month's figures: the preview's basis and the business totals. A failed read shows none. */
    private suspend fun loadMonth(workplaceIds: List<String>, month: YearMonth) {
        val business = getBusinessMonth(workplaceIds, month.year, month.month.number).getOrNull() ?: return
        val basis = AllocationBasis.entries.associateWith { b ->
            business.workplaces.mapNotNull { wm -> wm.financials?.let { wm.workplaceId to it.basisFor(b) } }.toMap()
        }
        _uiState.update { it.copy(business = business, basis = basis) }
    }

    /** The rule named by the route, once: the editor opens filled in from it. */
    private suspend fun offerConversion(ownerId: String) {
        val ruleId = convertRuleId ?: return
        if (conversionOffered) return
        conversionOffered = true
        val rule = getRecurringCostRules?.invoke(ownerId)?.getOrNull()?.firstOrNull { it.id == ruleId }
            ?.takeIf { it.kind == LedgerKind.COST } ?: return
        _uiState.update { it.copy(converting = rule) }
    }

    private fun save(cost: SharedCost, replacesRuleId: String?) {
        if (_uiState.value.isSaving) return
        val before = _uiState.value.costs
        // Optimistic: the card shows at once (a new one under a placeholder id until the re-read).
        val shown = if (cost.id.isBlank()) cost.copy(id = "$PENDING_ID_PREFIX${before.size}-${cost.name}") else cost
        _uiState.update { state ->
            state.copy(isSaving = true, costs = (before.filterNot { it.id == shown.id } + shown).sortedBy { it.name.lowercase() })
        }
        viewModelScope.launch {
            val user = getCurrentUserUseCase().first()
            if (user == null) {
                _uiState.update { it.copy(isSaving = false, costs = before) }
                return@launch
            }
            saveSharedCost(cost, replacesRuleId).fold(
                onSuccess = {
                    _uiState.update {
                        it.copy(
                            isSaving = false,
                            savedAt = it.savedAt + 1,
                            converting = if (replacesRuleId != null) null else it.converting,
                            successMessage = UiText.Resource(if (replacesRuleId != null) Res.string.shared_cost_converted else Res.string.shared_cost_saved)
                        )
                    }
                    refresh(user.uid)
                },
                onFailure = { e -> _uiState.update { it.copy(isSaving = false, costs = before, error = e.toUiText()) } }
            )
        }
    }

    private fun delete(costId: String) {
        if (costId.isPending) return
        val before = _uiState.value.costs
        _uiState.update { state -> state.copy(costs = before.filterNot { it.id == costId }) }
        viewModelScope.launch {
            val user = getCurrentUserUseCase().first() ?: return@launch _uiState.update { it.copy(costs = before) }
            deleteSharedCost(costId).fold(
                onSuccess = {
                    _uiState.update { it.copy(successMessage = UiText.Resource(Res.string.shared_cost_deleted)) }
                    refresh(user.uid)
                },
                onFailure = { e -> _uiState.update { it.copy(costs = before, error = e.toUiText()) } }
            )
        }
    }

    /** After a change: the costs (real ids) and this month's totals, without a spinner. */
    private suspend fun refresh(ownerId: String) {
        getSharedCosts(ownerId).onSuccess { costs -> _uiState.update { it.copy(costs = costs) } }
        val state = _uiState.value
        state.month?.let { loadMonth(state.workplaces.keys.toList(), it) }
    }

    companion object {
        fun monthOf(day: LocalDate): YearMonth = YearMonth(day.year, day.month)
    }
}
